import React from 'react';
import { motion } from 'framer-motion';
import { FiZap, FiClock, FiCheckCircle, FiX, FiGitBranch, FiAlertTriangle, FiSettings } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const ANAActions = () => {
  const beforeAfter = [
    {
      type: "before",
      title: "Before ANA Actions ❌",
      steps: [
        "Find a vulnerability in scan results",
        "Research the issue and potential solutions",
        "Write code changes manually",
        "Test the fix thoroughly",
        "Create pull request manually"
      ],
      time: "2-4 hours per issue",
      color: "text-red-500"
    },
    {
      type: "after",
      title: "With ANA Actions ✅",
      steps: [
        "Click the orange 'Fix' button",
        "Select which branch you want the fix in",
        "Let ANA create the pull request",
        "Review and merge"
      ],
      time: "2-3 minutes per issue",
      color: "text-green-500"
    }
  ];

  const stepByStep = [
    {
      number: 1,
      title: "Find Issues That Can Be Fixed",
      description: "Look for orange 'Fix' buttons in your scan results",
      details: [
        "Go to any security module (we'll use SCA as an example)",
        "Click on a project to see detailed scan results",
        "Look for orange 'Fix' buttons in the rightmost column"
      ],
      image: "/screenshots/security-modules/06_sca.png",
      note: "You'll see a table of vulnerabilities with 'Fix' buttons for issues that ANA can automatically resolve."
    },
    {
      number: 2,
      title: "Start the Fix Process",
      description: "Click the Fix button to begin",
      details: [
        "Click the orange 'Fix' button next to any vulnerability",
        "A dialog will appear asking where you want the fix"
      ],
      dialogInfo: {
        title: "Select Branch",
        question: "Where would you like Ana to create the pull request?",
        description: "Select the target branch where you want Ana to propose the security fixes"
      }
    },
    {
      number: 3,
      title: "Choose Your Branch",
      description: "Select where you want the fix applied",
      details: [
        "Click the branch dropdown to see available options",
        "Search for your branch if you have many branches",
        "Select the branch where you want ANA to create the pull request"
      ],
      tip: "Choose your development branch for testing, or main branch for urgent fixes."
    },
    {
      number: 4,
      title: "Let ANA Work Its Magic",
      description: "ANA analyzes and creates the fix",
      details: [
        "Click 'Continue' to start the fix process",
        "ANA analyzes the vulnerability and determines the best fix",
        "ANA creates the code changes (like updating dependency versions)",
        "ANA creates a pull request with a detailed description of what was fixed"
      ]
    },
    {
      number: 5,
      title: "Review and Merge",
      description: "Complete the fix in your repository",
      details: [
        "Go to your repository (GitHub, Bitbucket, etc.)",
        "Review the pull request that ANA created",
        "Check the changes to make sure they look good",
        "Merge the pull request to apply the security fix"
      ],
      success: "That's it! Your security issue is now fixed."
    }
  ];

  const fixTypes = [
    {
      category: "Dependency Vulnerabilities (Most Common)",
      description: "ANA is excellent at fixing dependency issues by updating to secure versions",
      examples: [
        {
          name: "XStream: enabled processing external entities",
          severity: "High",
          current: "1.4.5",
          fixed: "1.4.9"
        },
        {
          name: "XStream: DoS when unmarshalling",
          severity: "High",
          current: "1.4.5",
          fixed: "1.4.10"
        },
        {
          name: "XStream: remote code execution",
          severity: "High",
          current: "1.4.5",
          fixed: "1.4.14-jdk7"
        }
      ]
    },
    {
      category: "Other Fix Types",
      description: "ANA can handle various types of security fixes",
      types: [
        "Configuration changes for security settings",
        "Code patches for specific vulnerability patterns",
        "Library replacements when updates aren't available"
      ]
    }
  ];

  const locations = [
    {
      module: "SCA (Software Composition Analysis)",
      description: "Most common place for fixes - dependency vulnerabilities",
      image: "/screenshots/security-modules/06_sca.png"
    },
    {
      module: "SAST (Static Application Security Testing)",
      description: "Code-level security fixes - coding pattern improvements",
      image: "/screenshots/security-modules/08_sast.png"
    },
    {
      module: "Secrets Detection",
      description: "Credential security fixes - moving hardcoded secrets to secure storage",
      image: "/screenshots/security-modules/07_secrets-detection.png"
    }
  ];

  const requirements = [
    {
      title: "Connected Repository",
      description: "Your project must be connected via Integrations",
      icon: <FiGitBranch />
    },
    {
      title: "Proper Permissions",
      description: "ANA needs permission to create branches and pull requests",
      icon: <FiSettings />
    },
    {
      title: "Fixable Issue",
      description: "Not all vulnerabilities can be automatically fixed (yet!)",
      icon: <FiCheckCircle />
    },
    {
      title: "Available Branches",
      description: "Your repository must have branches ANA can access",
      icon: <FiGitBranch />
    }
  ];

  const bestPractices = [
    {
      type: "do",
      title: "✅ Do This",
      practices: [
        "Review all automated fixes before merging",
        "Test fixes in development before production",
        "Use descriptive branch names for organization",
        "Monitor fix success and application stability",
        "Keep repositories connected and permissions updated"
      ]
    },
    {
      type: "dont",
      title: "❌ Avoid This",
      practices: [
        "Don't merge without reviewing - always check the changes",
        "Don't skip testing - automated doesn't mean untested",
        "Don't ignore failed fixes - investigate why they didn't work",
        "Don't forget to update your local branches after merging"
      ]
    }
  ];

  const troubleshooting = [
    {
      problem: "No Branch Options in Dropdown",
      issue: "The branch dropdown shows 'No options'",
      solutions: [
        "Check that your repository is connected in Integrations",
        "Verify ANA has permission to access your repository",
        "Make sure your repository has branches available"
      ]
    },
    {
      problem: "Fix Button Missing",
      issue: "No orange 'Fix' button appears for an issue",
      solutions: [
        "Not all vulnerabilities can be automatically fixed",
        "Check if the issue type is supported by ANA Actions",
        "Some complex issues require manual intervention"
      ]
    },
    {
      problem: "Pull Request Creation Failed",
      issue: "ANA couldn't create the pull request",
      solutions: [
        "Verify repository permissions and access",
        "Check if the target branch exists and is accessible",
        "Ensure your Git integration is working properly"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="ANA Actions" />
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            ANA Actions - Let AI Fix Your Security Issues
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
            ANA Actions is your AI-powered security assistant that automatically fixes vulnerabilities for you. Instead of spending hours researching and manually fixing security issues, you can now click a button and let ANA create a pull request with the fix ready for review.
          </p>
          <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6 max-w-2xl mx-auto">
            <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">
              Think of it as having a security expert on your team who works 24/7 and never gets tired!
            </p>
          </div>
        </div>

        {/* Before vs After */}
        <MotionDiv delay={0.1}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              How It Saves You Time
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {beforeAfter.map((scenario, index) => (
                <div key={index} className={`p-6 rounded-lg border-2 ${
                  scenario.type === 'before' 
                    ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20' 
                    : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                }`}>
                  <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                    {scenario.title}
                  </h3>
                  <ul className="space-y-3 mb-6">
                    {scenario.steps.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        {scenario.type === 'before' ? (
                          <FiX className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        )}
                        <span className="text-gray-700 dark:text-gray-300">{step}</span>
                      </li>
                    ))}
                  </ul>
                  <div className={`text-center p-3 rounded-lg ${
                    scenario.type === 'before' 
                      ? 'bg-red-100 dark:bg-red-800/30' 
                      : 'bg-green-100 dark:bg-green-800/30'
                  }`}>
                    <FiClock className={`w-5 h-5 mx-auto mb-2 ${scenario.color}`} />
                    <p className={`font-bold ${scenario.color}`}>
                      Time spent: {scenario.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Step-by-Step Guide */}
        <MotionDiv delay={0.2}>
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Step-by-Step: How to Use ANA Actions
            </h2>
            
            <div className="space-y-8">
              {stepByStep.map((step, index) => (
                <div key={index} className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Step {step.number}: {step.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {step.description}
                      </p>
                      
                      <ul className="space-y-2 mb-4">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                          </li>
                        ))}
                      </ul>
                      
                      {step.dialogInfo && (
                        <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-4 mb-4">
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Dialog Information:</h4>
                          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <li><strong>Title:</strong> "{step.dialogInfo.title}"</li>
                            <li><strong>Question:</strong> "{step.dialogInfo.question}"</li>
                            <li><strong>Description:</strong> "{step.dialogInfo.description}"</li>
                          </ul>
                        </div>
                      )}
                      
                      {step.tip && (
                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>Pro Tip:</strong> {step.tip}
                          </p>
                        </div>
                      )}
                      
                      {step.success && (
                        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4">
                          <p className="text-green-700 dark:text-green-300 font-semibold">
                            {step.success}
                          </p>
                        </div>
                      )}
                      
                      {step.image && (
                        <div className="mt-4">
                          <img 
                            src={step.image} 
                            alt={step.title}
                            className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full"
                          />
                        </div>
                      )}
                      
                      {step.note && (
                        <div className="mt-4 p-4 bg-primary-500/5 dark:bg-primary-500/10 rounded-lg border border-primary-500/20">
                          <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                            {step.note}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Fix Types */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              What Types of Issues Can ANA Fix?
            </h2>
            
            <div className="space-y-8">
              {fixTypes.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {category.description}
                  </p>
                  
                  {category.examples && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                        Example from real platform data:
                      </h4>
                      <div className="space-y-3">
                        {category.examples.map((example, idx) => (
                          <div key={idx} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900 dark:text-white">
                                  {example.name}
                                </h5>
                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${
                                  example.severity === 'High' 
                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                }`}>
                                  {example.severity} severity
                                </span>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Current version: <span className="font-mono">{example.current}</span> → Fixed version: <span className="font-mono text-green-600 dark:text-green-400">{example.fixed}</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {category.types && (
                    <ul className="space-y-2">
                      {category.types.map((type, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700 dark:text-gray-300">{type}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Where to Find ANA Actions */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Where to Find ANA Actions
            </h2>
            
            <div className="space-y-6">
              {locations.map((location, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {location.module}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {location.description}
                  </p>
                  <img 
                    src={location.image} 
                    alt={location.module}
                    className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Requirements */}
        <MotionDiv delay={0.5}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Requirements for ANA Actions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {requirements.map((req, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <div className="text-primary-500 text-xl">
                    {req.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {req.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {req.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Check Your Integrations
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Make sure your repositories are connected:
              </p>
              <img 
                src="/screenshots/integrations/12_integrations.png" 
                alt="Integrations"
                className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full"
              />
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.6}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Best Practices for Using ANA Actions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {bestPractices.map((section, index) => (
                <div key={index} className={`p-6 rounded-lg border-2 ${
                  section.type === 'do' 
                    ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' 
                    : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                }`}>
                  <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.practices.map((practice, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        {section.type === 'do' ? (
                          <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <FiX className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        )}
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Troubleshooting */}
        <MotionDiv delay={0.7}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Troubleshooting Common Issues
            </h2>
            
            <div className="space-y-6">
              {troubleshooting.map((item, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <FiAlertTriangle className="w-6 h-6 text-yellow-500 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {item.problem}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        <strong>Problem:</strong> {item.issue}
                      </p>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-2">Solution:</p>
                        <ul className="space-y-2">
                          {item.solutions.map((solution, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 dark:text-gray-300">{solution}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={0.8}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to start fixing security issues automatically?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span className="text-gray-700 dark:text-gray-300">Go to the SCA module</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span className="text-gray-700 dark:text-gray-300">Find a project with issues</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span className="text-gray-700 dark:text-gray-300">Click an orange "Fix" button</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span className="text-gray-700 dark:text-gray-300">Watch ANA work its magic!</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a 
                href="/sca" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiZap className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Start with SCA</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Most fixable issues are here</p>
              </a>
              
              <a 
                href="/dashboard" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Check Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">See your overall security status</p>
              </a>
              
              <a 
                href="/integrations" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect your repositories</p>
              </a>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default ANAActions;
